import React, { useState, useEffect, useCallback } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { KeyRound, Plus, X, Loader2, Trash2 } from 'lucide-react';

const PasskeyManager = ({ isOpen, onClose }) => {
    const [credentials, setCredentials] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const loadCredentials = useCallback(async () => {
        try {
            const response = await fetch('/api/webauthn/my-credentials', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setCredentials(data);
            }
        } catch (err) {
            console.error('Erreur chargement passkeys:', err);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadCredentials();
            setError('');
            setSuccess('');
        }
    }, [isOpen, loadCredentials]);

    const handleAddPasskey = async () => {
        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            // Étape 1 : récupérer les options d'enregistrement depuis le serveur
            const optionsResponse = await fetch('/api/webauthn/generate-registration-options', {
                credentials: 'include',
            });

            if (!optionsResponse.ok) {
                throw new Error("Impossible de générer les options d'enregistrement");
            }
            const options = await optionsResponse.json();

            // Étape 2 : déclencher la cérémonie WebAuthn (Windows Hello / clé physique / coffre)
            // @simplewebauthn/browser gère tout l'encodage base64url automatiquement
            const registrationResponse = await startRegistration({ optionsJSON: options });

            // Étape 3 : demander un nom lisible pour ce passkey
            const friendlyName = window.prompt(
                'Donnez un nom à ce passkey (ex: "Laptop pro")',
                'Mon passkey'
            ) || 'Passkey sans nom';

            // Étape 4 : envoyer la réponse au serveur pour vérification et enregistrement
            const verifyResponse = await fetch('/api/webauthn/verify-registration', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: registrationResponse, friendlyName }),
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.verified) {
                setSuccess('Passkey enregistré avec succès !');
                loadCredentials();
            } else {
                setError(verifyData.message || "Échec de l'enregistrement");
            }
        } catch (err) {
            console.error('Erreur enregistrement passkey:', err);
            if (err.name === 'InvalidStateError') {
                setError('Ce passkey est déjà enregistré sur cet appareil.');
            } else if (err.name === 'NotAllowedError') {
                setError('Enregistrement annulé ou refusé.');
            } else {
                setError(err.message || 'Une erreur est survenue');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce passkey ? Vous ne pourrez plus l\'utiliser pour vous connecter à Solganeo.')) return;
        setError('');
        setSuccess('');
        try {
            const response = await fetch(`/api/webauthn/my-credentials/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (response.ok) {
                setSuccess('Passkey supprimé.');
                loadCredentials();
            } else {
                const data = await response.json().catch(() => ({}));
                setError(data.message || 'Suppression impossible');
            }
        } catch (err) {
            setError('Une erreur est survenue');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <KeyRound className="w-6 h-6 text-blue-500" />
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mes passkeys</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 text-sm text-red-500 bg-red-50 dark:bg-red-900/30 rounded-xl">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mb-4 p-3 text-sm text-green-600 bg-green-50 dark:bg-green-900/30 rounded-xl">
                        {success}
                    </div>
                )}

                <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                    {credentials.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                            Aucun passkey enregistré pour l'instant.
                        </p>
                    ) : (
                        credentials.map((cred) => (
                            <div
                                key={cred.id}
                                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl"
                            >
                                <KeyRound className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 truncate">
                                    {cred.friendlyName || 'Passkey sans nom'}
                                </span>
                                <button
                                    onClick={() => handleDelete(cred.id)}
                                    title="Supprimer ce passkey"
                                    className="text-gray-400 hover:text-red-500 flex-shrink-0"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))
                    )}
                </div>

                <button
                    onClick={handleAddPasskey}
                    disabled={isLoading}
                    className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-white font-medium
                        ${isLoading
                            ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-400 to-sky-400 hover:from-blue-500 hover:to-sky-500'
                        } transition-all duration-200`}
                >
                    {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <Plus className="w-5 h-5" />
                    )}
                    {isLoading ? 'Enregistrement...' : 'Ajouter un passkey'}
                </button>
            </div>
        </div>
    );
};

export default PasskeyManager;